import { Query } from '@apollo/client/react/components';
import DateTimeLabel from '@monorepo/common/component/DateTimeLabel';
import FilterableList from '@monorepo/common/component/FilterableList';
import MoneyLabel from '@monorepo/common/component/MoneyLabel';
import { useStateSetAsArray } from '@monorepo/common/hook/useStateSetAsArray';
import Sequence from '@monorepo/common/util/Sequence';
import React, { useState } from 'react';
import { Badge, Card, CardBody, CardHeader, CardTitle, Col, Collapse, CustomInput, Row } from 'reactstrap';
import {
  GatewayTransactionCsobRefreshErrorEnum,
  GatewayTransactionCsobStatusEnum,
  OrdersDocument,
  OrdersQuery,
  OrdersQueryVariables,
  OrderTotalStatusEnum
} from '../../graphql-operations';
import { colorForStatus } from './OrderDetailView';
import TransactionRefreshError from './transaction/csob/TransactionRefreshError';
import TransactionStatus from './transaction/csob/TransactionStatus';

interface OrderRowProps {
  order: {
    status: OrderTotalStatusEnum;
    orderNumber: string;
    created: string;
    totalPriceDiscounted: number;
  };
}

function OrderRow({ order }: OrderRowProps) {
  return (
    <span>
      {order.orderNumber} | <DateTimeLabel dateTimeString={order.created} /> |{' '}
      <MoneyLabel amount={order.totalPriceDiscounted} />
    </span>
  );
}

function SmallLabelForCheckbox({ children }: { children: React.ReactNode }) {
  // custom padding to accommodate "small" text alignment with checkbox
  return (
    <div className="small clickable" style={{ paddingTop: '3px' }}>
      {children}
    </div>
  );
}

interface FilterSectionProps {
  txStatuses: GatewayTransactionCsobStatusEnum[];
  addTxStatus: (status: GatewayTransactionCsobStatusEnum) => void;
  removeTxStatus: (status: GatewayTransactionCsobStatusEnum) => void;

  txErrors: GatewayTransactionCsobRefreshErrorEnum[];
  addTxError: (status: GatewayTransactionCsobRefreshErrorEnum) => void;
  removeTxError: (status: GatewayTransactionCsobRefreshErrorEnum) => void;
}

function FilterSection({
  txStatuses,
  addTxStatus,
  removeTxStatus,
  txErrors,
  addTxError,
  removeTxError
}: FilterSectionProps) {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  return (
    <Card>
      <CardHeader onClick={() => setIsFilterOpen(prevOpen => !prevOpen)} className="clickable">
        <Row>
          <Col xs="auto">Filtr</Col>
          <Col className="text-right">
            <Badge>Aktivní: {txStatuses.length + txErrors.length}</Badge>
          </Col>
        </Row>
      </CardHeader>

      <Collapse isOpen={isFilterOpen}>
        <CardBody>
          <CardTitle>
            <h6>Stavy transakce</h6>
          </CardTitle>

          {Object.values(GatewayTransactionCsobStatusEnum).map(status => {
            return (
              <Row key={status}>
                <Col>
                  <CustomInput
                    id={Sequence.getNext(status)}
                    type="checkbox"
                    checked={txStatuses.some(s => s === status)}
                    onChange={e => {
                      if (e.target.checked) {
                        addTxStatus(status);
                      } else {
                        removeTxStatus(status);
                      }
                    }}
                    label={
                      <SmallLabelForCheckbox>
                        <TransactionStatus status={status} />
                      </SmallLabelForCheckbox>
                    }
                  />
                </Col>
              </Row>
            );
          })}

          <h6 className="mt-3">Stavy automatické aktualizace</h6>

          {Object.values(GatewayTransactionCsobRefreshErrorEnum).map(error => {
            return (
              <Row key={error}>
                <Col>
                  <CustomInput
                    id={Sequence.getNext(error)}
                    type="checkbox"
                    checked={txErrors.some(e => e === error)}
                    onChange={e => {
                      if (e.target.checked) {
                        addTxError(error);
                      } else {
                        removeTxError(error);
                      }
                    }}
                    label={
                      <SmallLabelForCheckbox>
                        <TransactionRefreshError error={error} />
                      </SmallLabelForCheckbox>
                    }
                  />
                </Col>
              </Row>
            );
          })}
        </CardBody>
      </Collapse>
    </Card>
  );
}

interface OrderFilterableListProps {
  orderNumber?: string;
  url: string;
}

export default function OrderFilterableList({ orderNumber, url }: OrderFilterableListProps) {
  const [filter, setFilter] = useState('');
  const [txStatuses, addTxStatus, removeTxStatus] = useStateSetAsArray([] as GatewayTransactionCsobStatusEnum[]);
  const [txErrors, addTxError, removeTxError] = useStateSetAsArray([] as GatewayTransactionCsobRefreshErrorEnum[]);

  return (
    <Query<OrdersQuery, OrdersQueryVariables> query={OrdersDocument} variables={{ filter, txStatuses, txErrors }}>
      {({ data, loading, error, fetchMore }) => {
        const orders = data?.orders;

        return (
          <FilterableList
            items={orders?.items}
            loading={loading}
            error={error}
            selectedId={orderNumber}
            idExtractor={order => order.orderNumber}
            urlExtractor={order => `${url}/${order.orderNumber}`}
            colorExtractor={order => colorForStatus(order.status)}
            filter={filter}
            filterSectionRender={() => (
              <FilterSection
                txStatuses={txStatuses}
                addTxStatus={addTxStatus}
                removeTxStatus={removeTxStatus}
                txErrors={txErrors}
                addTxError={addTxError}
                removeTxError={removeTxError}
              />
            )}
            hasNext={!!orders?.next}
            onFilterChange={setFilter}
            onMoreClick={() => {
              fetchMore({
                variables: {
                  page: orders?.next?.page,
                  pageSize: orders?.next?.size,
                  txStatuses,
                  txErrors
                },
                updateQuery: (prev, { fetchMoreResult }) => {
                  if (!fetchMoreResult) {
                    return prev;
                  }
                  return {
                    ...fetchMoreResult,
                    orders: {
                      ...fetchMoreResult.orders,
                      items: [...prev.orders.items, ...fetchMoreResult.orders.items]
                    }
                  };
                }
              });
            }}
          >
            {order => <OrderRow order={order} />}
          </FilterableList>
        );
      }}
    </Query>
  );
}
